/**
 * Elite & Elite+ Entitlement Utilities for Frontend
 * Patch D: Client-side entitlement checks for UX
 *
 * IMPORTANT: These are UX helpers only.
 * The backend is the source of truth for entitlements.
 * Always handle 403 responses gracefully.
 */

// =============================================================================
// TIER HELPERS (supports legacy flags + new entitlements structure)
// =============================================================================

/**
 * Get the user's subscription tier
 * @param {Object} user - User object from auth context
 * @returns {string} - "free" | "elite" | "elite_plus"
 */
export function getSubscriptionTier(user) {
  if (!user) return "free";
  return user.subscription_tier || "free";
}

/**
 * Check if user has active Elite entitlement (Elite OR Elite+)
 *
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if user has active Elite access
 */
export function isEliteActive(user) {
  if (!user) return false;

  // Preferred: structured entitlements
  const ent = user.entitlements;
  if (ent?.elite?.active === true) return true;
  if (ent?.elite_plus?.active === true) return true;

  // Legacy flags
  if (user.elite_active === true) return true;
  if (user.is_elite === true) return true;
  if (user.is_elite_plus === true) return true;

  // Tier-based fallback
  const tier = getSubscriptionTier(user);
  return tier === "elite" || tier === "elite_plus";
}

/**
 * Check if user has active Elite+ entitlement (Elite+ only, not regular Elite)
 *
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if user has active Elite+ access
 */
export function isElitePlusActive(user) {
  if (!user) return false;

  // Preferred: structured entitlements
  const ent = user.entitlements;
  if (ent?.elite_plus?.active === true) return true;

  // Legacy boolean if present
  if (user.is_elite_plus === true) return true;
  if (user.elite_plus_active === true) return true;

  // Tier-based fallback
  const tier = getSubscriptionTier(user);
  return tier === "elite_plus";
}

/**
 * Check if user's tier meets or exceeds a required tier
 * Tier hierarchy: free < elite < elite_plus
 *
 * @param {Object} user - User object from auth context
 * @param {string} requiredTier - The minimum required tier
 * @returns {boolean} - True if user has the required tier or higher
 */
export function hasTierOrHigher(user, requiredTier) {
  const tier = getSubscriptionTier(user);
  const rank = { free: 0, elite: 1, elite_plus: 2 };
  return (rank[tier] ?? 0) >= (rank[requiredTier] ?? 0);
}

/**
 * Check if user has an active Lounge Pass (time-boxed access)
 *
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if user has an active lounge pass
 */
export function isLoungePassActive(user) {
  if (!user) return false;
  
  // Check new entitlements structure first
  const exp = user?.entitlements?.lounge?.expires_at || user?.lounge_expires_at;
  if (!exp) return false;
  
  const t = Date.parse(exp);
  if (Number.isNaN(t)) return false;
  return t > Date.now();
}

/**
 * Get the expiration time of the active lounge pass
 *
 * @param {Object} user - User object from auth context
 * @returns {string|null} - ISO timestamp of expiration or null if not set
 */
export function getLoungePassExpiresAt(user) {
  if (!user) return null;
  return user?.entitlements?.lounge?.expires_at || user?.lounge_expires_at || null;
}

/**
 * Check if user has Lounge access (either via subscription or active pass)
 *
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if user has any form of lounge access
 */
export function hasLoungeAccess(user) {
  // Elite/Elite+ get lounge access included
  if (isEliteActive(user)) return true;
  // Otherwise check for active pass
  return isLoungePassActive(user);
}

/**
 * Check if user is in Elite grace period (payment failed but still has access)
 *
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if user is in grace period
 */
export function isInGracePeriod(user) {
  if (!user) return false;

  // Check new entitlements structure
  if (user.entitlements?.elite?.in_grace) {
    return true;
  }

  // Fallback check
  return user.stripe_subscription_status === 'past_due' && isEliteActive(user);
}

/**
 * Check if Elite subscription is scheduled to cancel
 *
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if subscription will end at period end
 */
export function isScheduledToCancel(user) {
  if (!user) return false;

  // Check new entitlements structure
  if (user.entitlements?.elite?.scheduled_cancel) {
    return true;
  }

  // Fallback check
  return user.subscription_scheduled_cancel === true;
}

/**
 * Get Elite expiry date
 *
 * @param {Object} user - User object from auth context
 * @returns {Date|null} - Expiry date or null if not set/not Elite
 */
export function getEliteExpiryDate(user) {
  if (!user) return null;

  // Check new entitlements structure
  let expiresAt = user.entitlements?.elite?.expires_at;

  // Fallback to legacy fields
  if (!expiresAt) {
    expiresAt = user.plan_expires_at || user.plan_renews_at;
  }

  if (!expiresAt) return null;

  try {
    return new Date(expiresAt);
  } catch {
    return null;
  }
}

/**
 * Format Elite status message for display
 *
 * @param {Object} user - User object from auth context
 * @returns {Object} - { status: string, message: string, variant: string }
 */
export function getEliteStatusMessage(user) {
  if (!isEliteActive(user)) {
    return {
      status: 'inactive',
      message: 'Upgrade to Elite for premium features',
      variant: 'default',
    };
  }

  if (isInGracePeriod(user)) {
    return {
      status: 'grace',
      message: 'Payment failed - please update your payment method',
      variant: 'warning',
    };
  }

  if (isScheduledToCancel(user)) {
    const expiryDate = getEliteExpiryDate(user);
    const expiryStr = expiryDate
      ? expiryDate.toLocaleDateString()
      : 'end of billing period';
    return {
      status: 'canceling',
      message: `Elite access ends ${expiryStr}`,
      variant: 'info',
    };
  }

  return {
    status: 'active',
    message: 'Elite member',
    variant: 'success',
  };
}

/**
 * Handle 403 Elite-required response
 * Call this when an API returns 403 with X-Elite-Required header
 *
 * @param {Response} response - Fetch response object
 * @param {Function} navigate - React Router navigate function
 * @returns {boolean} - True if handled (should stop further processing)
 */
export function handleEliteRequiredError(response, navigate) {
  if (response.status === 403) {
    const eliteRequired = response.headers?.get('X-Elite-Required');
    if (eliteRequired === 'true') {
      // Redirect to upgrade page
      if (navigate) {
        navigate('/elite?reason=required');
      }
      return true;
    }
  }
  return false;
}

/**
 * Create axios interceptor for Elite-required responses
 * Add this to your axios instance to automatically handle Elite gating
 *
 * Usage:
 *   import apiClient from "../api/client";
 *   import { createEliteInterceptor } from './utils/entitlements';
 *
 *   const api = axios.create({ baseURL: '/api' });
 *   createEliteInterceptor(api, navigate);
 *
 * @param {Object} axiosInstance - Axios instance
 * @param {Function} navigate - React Router navigate function
 */
export function createEliteInterceptor(axiosInstance, navigate) {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 403) {
        const eliteRequired = error.response.headers?.['x-elite-required'];
        if (eliteRequired === 'true') {
          console.warn('[Elite] Access denied - Elite membership required');
          if (navigate) {
            navigate('/elite?reason=required');
          }
        }
      }
      return Promise.reject(error);
    }
  );
}

/**
 * Check if user can use Video Chat (Elite-only feature)
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if user can use video chat
 */
export function canUseVideoChat(user) {
  return isEliteActive(user);
}

/**
 * Check if user has completed identity verification
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if identity is verified
 */
export function isIdentityVerified(user) {
  if (!user) return false;
  
  // Check new identity object
  const identity = user.identity || {};
  if (identity.status === 'verified') return true;
  
  // Legacy fallback
  if (user.identity_verification_status === 'verified') return true;
  if (user.identity_verified === true) return true;
  
  return false;
}

/**
 * Check if user can send voice notes (identity-gated)
 * Voice notes are available to verified members only.
 * This keeps voice interactions human.
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if user can send voice notes
 */
export function canSendVoice(user) {
  return isIdentityVerified(user);
}

/**
 * Check if user can speak (post/reply) in rooms (identity-gated)
 * Rooms are listen-only for unverified users.
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if user can post/reply in rooms
 */
export function canSpeakInRooms(user) {
  return isIdentityVerified(user);
}

/**
 * Get the appropriate shop redirect URL for a gated feature
 * @param {string} reason - Reason code for the redirect
 * @returns {string} - Shop URL with focus and reason params
 */
export function getEliteUpgradeUrl(reason = 'feature') {
  return `/shop?focus=elite&reason=${encodeURIComponent(reason)}`;
}

export default {
  isEliteActive,
  isInGracePeriod,
  isScheduledToCancel,
  getEliteExpiryDate,
  getEliteStatusMessage,
  handleEliteRequiredError,
  createEliteInterceptor,
  canUseVideoChat,
  canSendVoice,
  canSpeakInRooms,
  isIdentityVerified,
  getEliteUpgradeUrl,
};
